import { useCallback } from 'react';
import { useCustomInstance } from '@/lib/use-custom-instance';

export interface ForgotPasswordRequestDto {
  email: string;
}

/**
 * `POST /auth/forgot-password` — request a branded Crown Prediction password-reset email.
 *
 * Hand-written to mirror the Orval-generated mutator hooks (see `src/lib/endpoints.ts`):
 * the backend endpoint is new and Orval regenerates from a *running* backend
 * (`orval.config.ts` input → `localhost:4030/api/docs-yaml`). Once the backend OpenAPI is
 * regenerated with `yarn generate-api`, this can be replaced by the generated
 * `useAuthenticationForgotPassword` hook + `ForgotPasswordRequestDto` model.
 *
 * The endpoint is enumeration-safe: it always resolves 204 whether or not the email is
 * registered, so callers must render an identical confirmation for any valid email.
 */
export const useForgotPassword = () => {
  const request = useCustomInstance<void>();
  return useCallback(
    (dto: ForgotPasswordRequestDto, signal?: AbortSignal) =>
      request({
        url: `/auth/forgot-password`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: dto,
        signal,
      }),
    [request],
  );
};
